import { useDisclosure, useToast } from '@chakra-ui/react'
import { useAppKit, useAppKitAccount } from '@reown/appkit/react'
import { captureException } from '@sentry/react'
import type React from 'react'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import AddWalletModal from '../../../../components/AddWalletModal'
import PageWrapper from '../../../../components/Pagewrapper'
import Toast from '../../../../components/Toast'
import Tooltip from '../../../../components/Tooltip'
import UpdateWalletHostingTypeModal from '../../../../components/UpdateWalletHostingTypeModal'
import AsyncAlert from '../../../../containers/AsyncAlert'
import {
  useDeleteWalletMutation,
  useGetAllWalletsByUserIdQuery,
  useRetryWalletScreeningMutation,
} from '../../../../redux/services/coreApi'
import type { RootState } from '../../../../redux/store'
import type { WalletDto } from '../../../../types/coreApi-types'
import useAsyncAlert from '../../../../utils/useAsyncAlert'
import { WalletItem } from './components/WalletItem'

const WalletsScreen: React.FC = () => {
  const addWalletModal = useDisclosure()
  const updateHostingTypeModal = useDisclosure()
  const [importingConnectedWallet, setImportingConnectedWallet] =
    useState<boolean>(false)
  const [selectedWalletForEdit, setSelectedWalletForEdit] =
    useState<WalletDto | null>(null)

  const userInfo = useSelector((state: RootState) => state.session.user)
  const toast = useToast()
  const history = useHistory()

  const wallets = useGetAllWalletsByUserIdQuery({ userId: userInfo.id })

  const hasPendingScreening = useMemo(
    () => wallets.data?.some(w => w.screeningStatus === 'Pending') ?? false,
    [wallets.data]
  )

  useGetAllWalletsByUserIdQuery(
    { userId: userInfo.id },
    {
      pollingInterval: 5000,
      skip: !hasPendingScreening,
    }
  )

  const [
    deleteWallet,
    { isError: deleteWalletError, reset: resetDeleteWallet },
  ] = useDeleteWalletMutation()

  const [retryWalletScreening] = useRetryWalletScreeningMutation()

  const { openAlertAndAwaitConfirmation, alertProps } = useAsyncAlert<
    WalletDto,
    WalletDto
  >()

  const { open: openAppKit } = useAppKit()
  const { address: connectedAddress } = useAppKitAccount()

  const handleEditWallet = (wallet: WalletDto) => {
    setSelectedWalletForEdit(wallet)
    updateHostingTypeModal.onOpen()
  }

  const handleCloseEditModal = () => {
    updateHostingTypeModal.onClose()
    setSelectedWalletForEdit(null)
  }

  const handleRetryScreening = async (wallet: WalletDto) => {
    try {
      await retryWalletScreening({
        userId: userInfo.id,
        walletId: wallet.id,
      }).unwrap()
      toast({
        position: 'bottom-right',
        render: () => <Toast type="success">Wallet screening restarted</Toast>,
      })
    } catch (err) {
      captureException(err)
      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="error">Failed to retry wallet screening</Toast>
        ),
      })
    }
  }

  return (
    <PageWrapper title="Wallets" description="Browse your wallets" fixedWidth>
      <AddWalletModal
        isOpen={addWalletModal.isOpen}
        onClose={() => {
          setImportingConnectedWallet(false)
          addWalletModal.onClose()
        }}
        initialAddress={importingConnectedWallet ? connectedAddress : undefined}
      />

      <UpdateWalletHostingTypeModal
        isOpen={updateHostingTypeModal.isOpen}
        onClose={handleCloseEditModal}
        wallet={selectedWalletForEdit}
      />

      <div className="space-y-2">
        {wallets.data &&
          wallets.data.map((item, idx) => {
            return (
              <WalletItem
                key={idx}
                name={item.name}
                address={item.address}
                destinationTag={item.destinationTag}
                network={item.network}
                hostingType={item.hostingType}
                screeningStatus={item.screeningStatus}
                createdAt={item.createdAt}
                onPressDelete={async () => {
                  resetDeleteWallet()
                  await openAlertAndAwaitConfirmation(item, item)
                }}
                onPressEdit={() => handleEditWallet(item)}
                onPressRetryScreening={
                  !item.screeningStatus || item.screeningStatus === 'Error' || item.screeningStatus === 'Rejected'
                    ? () => handleRetryScreening(item)
                    : undefined
                }
                onPressVerify={
                  item.screeningStatus === 'Pending'
                    ? () => history.push(`/app/wallets/verify/${item.id}`)
                    : undefined
                }
              />
            )
          })}
      </div>

      <div className="mt-10 flex justify-center">
        <div className="space-y-4 w-full max-w-md">
          {!connectedAddress ? (
            <>
              <Tooltip
                label={
                  userInfo.kycDetails?.kycStatus !== 'Approved'
                    ? 'You need to have completed your KYC process to add a wallet'
                    : undefined
                }
              >
                <button
                  disabled={userInfo.kycDetails?.kycStatus !== 'Approved'}
                  onClick={() => {
                    setImportingConnectedWallet(false)
                    addWalletModal.onOpen()
                  }}
                  className="button w-full"
                >
                  Add wallet
                </button>
              </Tooltip>

              <Tooltip
                label={
                  userInfo.kycDetails?.kycStatus !== 'Approved'
                    ? 'You need to have completed your KYC process to connect a wallet'
                    : undefined
                }
              >
                <button
                  disabled={userInfo.kycDetails?.kycStatus !== 'Approved'}
                  onClick={() => openAppKit()}
                  className="button w-full"
                >
                  Connect your wallet
                </button>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip
                label={
                  userInfo.kycDetails?.kycStatus !== 'Approved'
                    ? 'You need to have completed your KYC process to add a wallet'
                    : undefined
                }
              >
                <button
                  disabled={userInfo.kycDetails?.kycStatus !== 'Approved'}
                  onClick={() => {
                    setImportingConnectedWallet(true)
                    addWalletModal.onOpen()
                  }}
                  className="button w-full"
                >
                  Add wallet
                </button>
              </Tooltip>

              <Tooltip
                label={
                  userInfo.kycDetails?.kycStatus !== 'Approved'
                    ? 'You need to have completed your KYC process to switch wallets'
                    : 'Switch/disconnect connected wallet'
                }
              >
                <button
                  disabled={userInfo.kycDetails?.kycStatus !== 'Approved'}
                  onClick={() => openAppKit()}
                  className="button w-[120px]"
                >
                  Switch or disconnect wallet
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <AsyncAlert
        title={deleteWalletError ? 'Error' : 'Delete this wallet?'}
        bodyText={wallet =>
          deleteWalletError
            ? 'You cannot delete your wallet. This is probably due to existing transfers associated to this wallet. Please contact support if you must delete or change this wallet.'
            : `Are you sure you want to delete wallet with address: ${wallet?.address}? Keep in mind you are not allowed to delete wallets that have associated transactions.`
        }
        submitButtonText={deleteWalletError ? 'Close' : 'Delete wallet'}
        onClick={async payload => {
          if (deleteWalletError) return

          if (payload) {
            const promise = deleteWallet(payload.id).unwrap()
            promise.catch(captureException)
            return promise
          }
        }}
        {...alertProps}
      />
    </PageWrapper>
  )
}

export default WalletsScreen
